import type { HttpRequestEntry, MetricSeries, RouteStat } from "../../types";

/**
 * Request semantics of the backend monitoring (pizza-backend src/monitoring/http.py), mirrored here so
 * the board explains what it shows: which routes wait by design, the slow threshold and the size of
 * the live ring the "last minute" counts come from.
 */

/** a request at least this long lands in the slow list (same threshold as the http_slow finding) */
export const SLOW_REQUEST_MS = 2000;

/**
 * Slow by design (long-poll wait for the evaluation, on-camera capture): counted, but kept out of the
 * overall latency histogram and of the slow list, so their duration is not alarming.
 */
export const SLOW_BY_DESIGN_ROUTES: ReadonlySet<string> = new Set([
  "/api/mobile/pizzas/<pizza_id>/evaluation",
  "/api/camera/<device_id>/photo",
]);

/** the backend keeps the last 300 requests; the "last minute" counts cannot exceed it */
export const RECENT_RING_SIZE = 300;

/** how many requests of the live tail the backend sends (newest first) */
export const TAIL_SIZE = 150;

export type StatusClass = "s2" | "s3" | "s4" | "s5";
export const STATUS_CLASSES: StatusClass[] = ["s2", "s3", "s4", "s5"];

export function statusClass(status: number): StatusClass {
  if (status >= 500) return "s5";
  if (status >= 400) return "s4";
  if (status >= 300) return "s3";
  return "s2";
}

/** "GET /api/x/<id>" -> method + path (a key without a method is all path) */
export function splitRoute(route: string): { method: string; path: string } {
  const space = route.indexOf(" ");
  if (space <= 0) return { method: "", path: route };
  return { method: route.slice(0, space), path: route.slice(space + 1) };
}

export const isSlowByDesign = (routePath: string) => SLOW_BY_DESIGN_ROUTES.has(routePath);

/** a request that is slow and not slow by design */
export const isSlowRequest = (entry: Pick<HttpRequestEntry, "ms" | "route">) =>
  entry.ms >= SLOW_REQUEST_MS && !isSlowByDesign(entry.route);

// --- route table ------------------------------------------------------------------------------------------
export type RouteSort = "requests" | "errors" | "p95" | "max";
export const ROUTE_SORTS: RouteSort[] = ["requests", "errors", "p95", "max"];

const desc = (a: number | null | undefined, b: number | null | undefined) => (b ?? -1) - (a ?? -1);

export function sortRoutes(routes: RouteStat[], sort: RouteSort): RouteStat[] {
  const out = [...routes];
  out.sort((a, b) => {
    switch (sort) {
      case "errors":
        return desc(a.s5, b.s5) || desc(a.s4, b.s4) || desc(a.n, b.n);
      case "p95":
        return desc(a.p95, b.p95) || desc(a.n, b.n);
      case "max":
        return desc(a.max, b.max) || desc(a.n, b.n);
      default:
        return desc(a.n, b.n) || a.route.localeCompare(b.route);
    }
  });
  return out;
}

// --- live tail --------------------------------------------------------------------------------------------
export type TailFilter = "all" | "errors" | "s4" | "s5";
export const TAIL_FILTERS: TailFilter[] = ["all", "errors", "s4", "s5"];

export function filterTail(entries: HttpRequestEntry[], filter: TailFilter, text: string): HttpRequestEntry[] {
  const needle = text.trim().toLowerCase();
  return entries.filter((entry) => {
    if (filter === "errors" && entry.status < 400) return false;
    if (filter === "s4" && (entry.status < 400 || entry.status >= 500)) return false;
    if (filter === "s5" && entry.status < 500) return false;
    if (!needle) return true;
    return [entry.method, entry.path, entry.route, String(entry.status), entry.ip ?? "", entry.device ?? ""]
      .some((value) => value.toLowerCase().includes(needle));
  });
}

/** a shortened id for narrow cells (the full id stays in the title / link) */
export const shortId = (id: string, length = 8) => (id.length > length + 1 ? `${id.slice(0, length)}…` : id);

/** minutes the metric window really covers (its last bucket is still running) */
export function coveredMinutes(data: MetricSeries, fetchedAt: number): number {
  const windowMs = data.t.length * data.step_ms;
  const elapsed = fetchedAt ? fetchedAt - data.start : windowMs;
  return Math.max(data.step_ms, Math.min(windowMs, elapsed)) / 60_000;
}
